import { useCallback } from 'react';

import { FAIconName, useToastMessage } from '@equip.health/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ApiCommand } from '~/lib/Api';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useApi } from '~/lib/hooks';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { validateSafeUrl } from '~/lib/utils/url.util';
import { useGetGroupSectionEventMeetingDetailsV2Mutation } from '~/store/services/groupClass';

const {
  groupClasses: {
    getGroupClassEventMeetingDetails: getGroupClassEventZoomMeetingDetailsUrl,
  },
} = urlConstants;

interface UseGroupEventMeetingDetailsReturn {
  openGroupEventMeeting: (eventExternalId: string) => void;
}

export const useGroupEventMeetingDetails =
  (): UseGroupEventMeetingDetailsReturn => {
    const { groupsRestructuring: isGroupsRestructuringEnabled } = useFlags();
    const { showErrorToast } = useErrorToast();
    const { createToast } = useToastMessage();

    const [getGroupSectionEventMeetingDetailsV2] =
      useGetGroupSectionEventMeetingDetailsV2Mutation();

    const { sendRequest: getGroupClassEventMeetingDetails } =
      useApi<BaseAPIResponse<MeetingDetails>>();

    const openMeeting = (meetingDetails: MeetingDetails) => {
      const urlToOpen = meetingDetails.joinUrl ?? meetingDetails.startUrl;
      const validatedUrl = validateSafeUrl(urlToOpen);

      if (!validatedUrl) {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: 'invalid-meeting-url-error',
          text: 'Invalid meeting URL',
          variant: 'critical',
        });
        return;
      }

      window.open(validatedUrl, '_blank', 'noopener');
    };

    const openGroupEventMeeting = useCallback(
      (eventExternalId: string) => {
        if (isGroupsRestructuringEnabled) {
          getGroupSectionEventMeetingDetailsV2(eventExternalId)
            .unwrap()
            .then((response) => {
              openMeeting(response);
            })
            .catch((error) => {
              createToast({
                icon: FAIconName.exclamationTriangleRegular,
                id: 'get-group-event-meeting-details-error',
                text: error.data?.message || 'Failed to get meeting details',
                variant: 'critical',
              });
            });
        } else {
          getGroupClassEventMeetingDetails({
            callback: (
              response: BaseAPIResponse<MeetingDetails>,
              statusCode: number,
              error: string,
            ): void => {
              if (statusCode === 200) {
                openMeeting(response.data);
              } else {
                showErrorToast(
                  GlobalErrorCode.GET_GROUP_CLASS_EVENT_MEETING_DETAILS,
                  statusCode,
                  error,
                );
              }
            },
            command: ApiCommand.POST,
            url: getGroupClassEventZoomMeetingDetailsUrl(eventExternalId),
          });
        }
      },
      [
        isGroupsRestructuringEnabled,
        getGroupSectionEventMeetingDetailsV2,
        getGroupClassEventMeetingDetails,
        createToast,
        showErrorToast,
      ],
    );

    return { openGroupEventMeeting };
  };
